import clsx from 'clsx'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './badge.scss?inline'

export type BadgeType =
  | 'neutral'
  | 'brand'
  | 'info'
  | 'positive'
  | 'warning'
  | 'negative'

export type BadgeSize = 'sm' | 'lg'

/**
 * `dap-ds-badge`
 * @summary A badge is a small status descriptor for UI elements.
 * @element dap-ds-badge
 * @title - Badge
 *
 * @slot - The content of the badge.
 * @slot icon - The icon of the badge.
 *
 * @csspart base - The main container of the badge.
 * @csspart icon - The icon of the badge.
 * @csspart content - The content of the badge.
 * @csspart icon-base - The base of the icon.
 *
 * @cssproperty --dds-badge-border-width - The width of the badge's border. Default is the design system's base border width.
 * @cssproperty --dds-badge-border-style - The style of the badge's border. Default is solid.
 * @cssproperty --dds-badge-border-radius - The border radius of the badge. Default is the design system's base radius.
 * @cssproperty --dds-badge-font-weight - The font weight of the badge text. Default is bold.
 * @cssproperty --dds-badge-line-height - The line height of the badge text. Default is 1.2.
 * @cssproperty --dds-badge-transition - The transition property for the badge. Default is 'all 0.2s ease-in-out'.
 *
 * @cssproperty --dds-badge-padding-sm - The padding of the small badge. Default is the design system's spacing-100 and spacing-200.
 * @cssproperty --dds-badge-padding-lg - The padding of the large badge. Default is the design system's spacing-100 and spacing-300.
 * @cssproperty --dds-badge-font-size-sm - The font size of the small badge. Default is the design system's font-xs.
 * @cssproperty --dds-badge-font-size-lg - The font size of the large badge. Default is the design system's font-sm.
 *
 * @cssproperty --dds-badge-neutral-border-color - The border color of the neutral badge. Default is the design system's border-neutral-base.
 * @cssproperty --dds-badge-neutral-background - The background color of the neutral badge. Default is the design system's background-neutral-medium.
 * @cssproperty --dds-badge-neutral-color - The text color of the neutral badge. Default is the design system's text-neutral-subtle.
 *
 * @cssproperty --dds-badge-brand-border-color - The border color of the brand badge. Default is the design system's border-brand-base.
 * @cssproperty --dds-badge-brand-background - The background color of the brand badge. Default is the design system's background-brand-medium.
 * @cssproperty --dds-badge-brand-color - The text color of the brand badge. Default is the design system's text-brand-subtle.
 *
 * @cssproperty --dds-badge-info-border-color - The border color of the info badge. Default is the design system's border-informative-base.
 * @cssproperty --dds-badge-info-background - The background color of the info badge. Default is the design system's background-informative-medium.
 * @cssproperty --dds-badge-info-color - The text color of the info badge. Default is the design system's text-informative-subtle.
 *
 * @cssproperty --dds-badge-positive-border-color - The border color of the positive badge. Default is the design system's border-positive-base.
 * @cssproperty --dds-badge-positive-background - The background color of the positive badge. Default is the design system's background-positive-medium.
 * @cssproperty --dds-badge-positive-color - The text color of the positive badge. Default is the design system's text-positive-subtle.
 *
 * @cssproperty --dds-badge-warning-border-color - The border color of the warning badge. Default is the design system's border-warning-subtle.
 * @cssproperty --dds-badge-warning-background - The background color of the warning badge. Default is the design system's background-warning-medium.
 * @cssproperty --dds-badge-warning-color - The text color of the warning badge. Default is the design system's text-warning-subtle.
 *
 * @cssproperty --dds-badge-negative-border-color - The border color of the negative badge. Default is the design system's border-negative-base.
 * @cssproperty --dds-badge-negative-background - The background color of the negative badge. Default is the design system's background-negative-medium.
 * @cssproperty --dds-badge-negative-color - The text color of the negative badge. Default is the design system's text-negative-subtle.
 */
@customElement('dap-ds-badge')
export default class DapDSBadge extends DdsElement {
  /** The type of the badge
   * @type { 'neutral' | 'brand' | 'info' | 'positive' | 'warning' | 'negative' }
   */
  @property() type: BadgeType = 'neutral'

  /** The size of the badge
   * @type { 'sm' | 'lg' }
   */
  @property() size: BadgeSize = 'sm'

  /** The icon of the badge, this is a name of a built in icon */
  @property() icon?: string

  /** Whether the badge represents dynamic content that should announce changes */
  @property({ type: Boolean }) live = false

  private _hasSlottedIcon = false
  private _cachedIconSize = 12

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    this.setAccessibilityAttributes()

    this._cachedIconSize = this.getIconSize(this.size)
    this.setupSlotListeners()
    this.updateIconSlot()
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (_changedProperties.has('size')) {
      this._cachedIconSize = this.getIconSize(this.size)
      this.updateIconSlot()
    }

    if (_changedProperties.has('live')) {
      this.setAccessibilityAttributes()
    }

    if (_changedProperties.has('icon')) {
      this.validateIcon()
    }
  }

  private setAccessibilityAttributes(): void {
    if (this.live) {
      this.setAttribute('role', 'status')
      this.setAttribute('aria-live', 'polite')
    } else {
      this.setAttribute('role', 'img')
      this.removeAttribute('aria-live')
    }
  }

  private validateIcon(): void {
    if (this.icon && typeof this.icon === 'string' && this.icon.trim() === '') {
      console.warn('Badge: Empty icon name provided')
    }
  }

  private setupSlotListeners(): void {
    try {
      if (!this.shadowRoot) return

      const iconSlot = this.shadowRoot.querySelector(
        'slot[name="icon"]',
      ) as HTMLSlotElement

      if (!iconSlot) return

      iconSlot.addEventListener('slotchange', () => {
        this.updateIconSlot()
        this.requestUpdate()
      })
    } catch (error) {
      console.warn('Failed to setup slot listeners:', error)
    }
  }

  private updateIconSlot(): void {
    try {
      if (!this.shadowRoot) return

      const iconSlot = this.shadowRoot.querySelector(
        'slot[name="icon"]',
      ) as HTMLSlotElement

      if (!iconSlot) {
        this._hasSlottedIcon = false
        return
      }

      const assignedElements = iconSlot.assignedElements()
      this._hasSlottedIcon = assignedElements.length > 0

      if (assignedElements.length > 0) {
        assignedElements[0].setAttribute(
          'staticSize',
          this._cachedIconSize.toString(),
        )
      }
    } catch (error) {
      console.warn('Failed to update icon slot:', error)
      this._hasSlottedIcon = false
    }
  }

  private getIconSize(size: BadgeSize): number {
    return size === 'sm' ? 12 : 16
  }

  private renderIcon() {
    if (this.icon) {
      return html`
        <dap-ds-icon
          part="icon"
          exportparts="icon, base:icon-base"
          name="${this.icon}"
          staticSize=${this._cachedIconSize}>
        </dap-ds-icon>
      `
    }

    return html`<slot name="icon" part="icon"></slot>`
  }

  render() {
    const hasIcon = this.icon || this._hasSlottedIcon

    return html`
      <div
        part="base"
        class=${clsx(
          'badge',
          `badge--${this.type}`,
          `badge--${this.size}`,
          hasIcon && 'badge--has-icon',
        )}>
        <dap-ds-stack direction="row" spacing="100">
          ${hasIcon ? this.renderIcon() : nothing}
          <slot part="content"></slot>
        </dap-ds-stack>
      </div>
    `
  }
}
